import { Issue } from "generated-graphql/graphql";

import type { AttentionItem as GenericAttentionItem } from "../ReviewInteraction";

import {
  PartialExtractionReviewFormData,
  displayValue,
  fieldPath,
  isFlagged,
  isRecordConflict,
  isReviewed,
  pairRecordRows,
  rowAnswerKey,
  rowCellKey,
  rowCellPath,
  rowDisplayName,
  rowPath,
  schemaFields,
  toRecordDrafts,
} from "./formData";
import {
  ExtractionRecordValues,
  ExtractionResult,
  ExtractionReviewSchema,
  extractedBaseline,
  rowConfidenceKey,
  toDraftValue,
} from "./types";

/**
 * Why a control wants the reviewer's attention.
 *
 * Four kinds because there are four reasons, and they read differently: the
 * extractor was unsure, this value would replace a different one on the record,
 * the record holds a whole row this reading does not, or the caller's validator
 * objected.
 */
export type AttentionKind = "issue" | "recordConflict" | "recordRow" | "lowConfidence";

/** Every kind, so a count can report a zero rather than an absence. */
export const ATTENTION_KINDS: readonly AttentionKind[] = [
  "issue",
  "recordConflict",
  "recordRow",
  "lowConfidence",
];

/** One thing the reviewer has to look at, and where it is. */
export type AttentionItem = GenericAttentionItem<AttentionKind>;

/**
 * Everything wanting attention, in the order the reviewer reads the form.
 *
 * One walk rather than one per affordance: the banner's counts, the attention
 * list and "jump to next" all come from this, so they cannot disagree about what
 * is unresolved or what order it is in. It shares `isFlagged`, `isReviewed` and
 * `isRecordConflict` with the per-field indicators for the same reason.
 *
 * A field can appear more than once — an unsure reading that also disagrees with
 * the record is two reasons to look at one control — so counts come from the
 * items and travel de-duplicates by path.
 */
export function listAttention({
  schema,
  extraction,
  record,
  data,
  confidence,
  lowConfidenceThreshold,
  issues,
  acknowledged,
}: {
  schema: ExtractionReviewSchema;
  extraction: ExtractionResult;
  record: ExtractionRecordValues | undefined;
  data: PartialExtractionReviewFormData;
  confidence: Map<string, number>;
  lowConfidenceThreshold: number;
  /** The caller's issues, already re-keyed onto form paths by `toFormIssues`. */
  issues: readonly Issue[];
  /**
   * Paths the reviewer has answered — kept the document's value over the record's,
   * or confirmed an unsure reading reads right. One acknowledgement covers the
   * field: having looked at it is having looked at it, whichever of the two
   * prompted them. A validation issue is not theirs to dismiss, so it survives.
   */
  acknowledged?: ReadonlySet<string>;
}): AttentionItem[] {
  const baseline = extractedBaseline(extraction);
  const recordValues = toRecordDrafts(schemaFields(schema), record?.values);
  const issuesByPath = new Map<string, Issue[]>();
  for (const issue of issues) {
    if (issue.key == null) continue;
    const existing = issuesByPath.get(issue.key);
    if (existing) existing.push(issue);
    else issuesByPath.set(issue.key, [issue]);
  }

  const items: AttentionItem[] = [];

  for (const field of schema.sections.flatMap((section) => section.fields)) {
    items.push(
      ...cellAttention({
        path: fieldPath(field.key),
        label: field.label,
        spec: field,
        current: data.values?.[field.key],
        extracted: toDraftValue(field, baseline.values?.[field.key]),
        recordValue: recordValues.get(field.key),
        hasCandidate: true,
        acknowledgeKey: field.key,
        score: confidence.get(field.key) ?? null,
        lowConfidenceThreshold,
        issuesByPath,
        acknowledged,
      })
    );
  }

  for (const group of schema.rowGroups ?? []) {
    items.push(
      ...rowGroupAttention({
        group,
        extraction,
        record,
        data,
        confidence,
        lowConfidenceThreshold,
        issuesByPath,
        acknowledged,
      })
    );
  }

  // An issue whose key matches no control still has to be reachable in the list,
  // even though there is nowhere to travel to — `toFormIssues` keeps an unmatched
  // key rather than dropping it, and silently losing it here would undo that.
  const addressed = new Set(items.filter((item) => item.kind === "issue").map((item) => item.path));
  for (const [path, pathIssues] of issuesByPath) {
    if (addressed.has(path)) continue;
    for (const issue of pathIssues) {
      // The message alone: the key is a react-hook-form path, and
      // `rows.constituents.2.values.casNumber — ...` in front of it reads as
      // machinery rather than as the problem.
      items.push({ kind: "issue", path: "", label: issue.message });
    }
  }

  return items;
}

/** The reasons one control wants attention, in the order they are worth reading. */
function cellAttention({
  path,
  label,
  spec,
  current,
  extracted,
  recordValue,
  hasCandidate,
  score,
  lowConfidenceThreshold,
  issuesByPath,
  acknowledged,
  acknowledgeKey,
}: {
  path: string;
  label: string;
  spec: Parameters<typeof isReviewed>[0];
  current: Parameters<typeof isReviewed>[1];
  extracted: Parameters<typeof isReviewed>[2];
  recordValue: Parameters<typeof isReviewed>[2] | undefined;
  /** False on a row the reading never proposed, as the grid reads it. */
  hasCandidate: boolean;
  score: number | null;
  lowConfidenceThreshold: number;
  issuesByPath: Map<string, Issue[]>;
  acknowledged: ReadonlySet<string> | undefined;
  /** How an answer about this control is remembered, or null when it cannot be. */
  acknowledgeKey: string | null;
}): AttentionItem[] {
  const items: AttentionItem[] = [];
  const reviewed = hasCandidate ? isReviewed(spec, current, extracted) : true;

  // The validator's objection first: it is the only one saying the value is
  // wrong rather than worth checking.
  for (const issue of issuesByPath.get(path) ?? []) {
    items.push({ kind: "issue", path, label, detail: issue.message });
  }

  if (
    !(acknowledgeKey != null && acknowledged?.has(acknowledgeKey)) &&
    isRecordConflict({ spec, current, extracted, recordValue, hasCandidate })
  ) {
    items.push({
      kind: "recordConflict",
      path,
      label,
      answerKey: acknowledgeKey ?? undefined,
      detail: `saved: ${displayValue(spec, recordValue)}; this review: ${displayValue(spec, current)}`,
    });
  }

  if (
    !(acknowledgeKey != null && acknowledged?.has(acknowledgeKey)) &&
    isFlagged(score, reviewed, lowConfidenceThreshold)
  ) {
    items.push({
      kind: "lowConfidence",
      path,
      label,
      answerKey: acknowledgeKey ?? undefined,
      detail:
        score == null
          ? "the extractor was unsure"
          : `the extractor was ${Math.round(score * 100)}% sure`,
    });
  }

  return items;
}

/** One repeated group's cells and its record-only rows, in reading order. */
function rowGroupAttention({
  group,
  extraction,
  record,
  data,
  confidence,
  lowConfidenceThreshold,
  issuesByPath,
  acknowledged,
}: {
  group: NonNullable<ExtractionReviewSchema["rowGroups"]>[number];
  extraction: ExtractionResult;
  record: ExtractionRecordValues | undefined;
  data: PartialExtractionReviewFormData;
  confidence: Map<string, number>;
  lowConfidenceThreshold: number;
  issuesByPath: Map<string, Issue[]>;
  acknowledged: ReadonlySet<string> | undefined;
}): AttentionItem[] {
  const items: AttentionItem[] = [];
  const baseline = extractedBaseline(extraction);
  // Paired against the seed rather than the extractor's own reading, for the
  // reason `listAttention`'s caller seeds from it: the seed is what the grid is
  // showing.
  const { byExtractedIndex } = pairRecordRows(
    group,
    extraction.rows?.[group.key],
    record?.rows?.[group.key]
  );

  const rows = data.rows?.[group.key] ?? [];
  rows.forEach((row, index) => {
    const rowLabel = `${group.rowNoun} ${index + 1}`;
    if (row?.origin === "record") {
      // Keeping the row is an answer, like keeping a value is: without it the
      // count cannot reach zero for a reviewer who decides the row belongs, and
      // travel keeps landing on a question already settled.
      if (!acknowledged?.has(rowAnswerKey(group, rows, index))) {
        items.push({
          kind: "recordRow",
          path: rowPath(group.key, index),
          // Named, not numbered. This line is the only place a row is described
          // away from its own box, and "constituent 3" asks the reviewer to
          // count boxes to find out which constituent is on the record.
          label: rowDisplayName(group, row.values, index),
          answerKey: rowAnswerKey(group, rows, index),
          detail: `saved on the record, but this document does not list it`,
        });
      }
      // No return: a validation issue can address one of this row's cells, and
      // that input is on screen and editable, so it has somewhere to travel to.
      // Neither warning can be raised on them — no extracted reading behind them
      // — so nothing else follows from falling through.
    }

    const extractedIndex = row?.extractedIndex;
    const recordRow = extractedIndex == null ? undefined : byExtractedIndex.get(extractedIndex);
    // The extractor's own reading, exactly as the cell's own indicator reads it
    // — not the seed. The two differ as soon as a correction has been saved, and
    // taking the seed here would judge a cell against the reviewer's own earlier
    // answer: a corrected low-confidence value would count as unreviewed, so the
    // banner would report a value with no warning anywhere on the form and
    // travel would stop on a clean field. The pairing above is the one thing
    // that does read the seed, because that is what the grid renders.
    const extractedRow =
      extractedIndex == null ? undefined : baseline.rows?.[group.key]?.[extractedIndex];
    for (const column of group.columns) {
      items.push(
        ...cellAttention({
          path: rowCellPath(group.key, index, column.key),
          label: `${column.label} (${rowLabel})`,
          spec: column,
          current: row.values?.[column.key],
          extracted: toDraftValue(column, extractedRow?.[column.key]),
          recordValue: recordRow == null ? undefined : toDraftValue(column, recordRow[column.key]),
          hasCandidate: extractedIndex != null,
          // Keyed to the extracted row, not the row's current position: an
          // answer the reviewer gave has to survive a row above it being
          // removed.
          acknowledgeKey:
            extractedIndex == null ? null : rowCellKey(group.key, extractedIndex, column.key),
          score:
            extractedIndex == null
              ? null
              : (confidence.get(rowConfidenceKey(group.key, extractedIndex, column.key)) ?? null),
          lowConfidenceThreshold,
          issuesByPath,
          acknowledged,
        })
      );
    }
  });
  return items;
}
